import { requestOnce } from "services/api";

// Helper kontrak Bulk API BE (lihat "11a-accounting-bulk-api-frontend-integration.md"):
// - 1 request tanpa retry otomatis (bulk tidak boleh terkirim dua kali),
// - 200 + success:true juga untuk sukses sebagian -> cek summary.all_ok,
// - 422 + data.summary.rolled_back -> atomic gagal, tidak ada yang tersimpan,
// - results[i].index = posisi item di request, status "ok" | skipped | duplicate | not_found | conflict | ...
export const BULK_LIMIT = 500;

const sendBulk = async (url, method, payload) => {
  try {
    return await requestOnce(url, method, payload);
  } catch (error) {
    // Route didaftarkan dengan method lain (mis. PUT vs PATCH): 405 berarti tidak ada yang diproses, aman dicoba ulang.
    if (error?.status === 405 && method === "PATCH") return requestOnce(url, "PUT", payload);
    throw error;
  }
};

const normalizeBulkResponse = (body) => {
  const data = body?.data || {};
  const results = Array.isArray(data.results) ? data.results : [];
  const summary = data.summary || {};
  return {
    message: body?.message || "",
    summary,
    results,
    rolledBack: Boolean(summary.rolled_back),
    allOk: summary.all_ok ?? results.every((result) => result.status === "ok"),
  };
};

// Jalankan bulk request. Respons atomic yang di-rollback (422) dikembalikan sebagai hasil (rolledBack: true),
// error level request (validasi payload, 401/403/500) tetap dilempar.
export const runBulkRequest = async ({ url, method = "POST", payload }) => {
  try {
    return normalizeBulkResponse(await sendBulk(url, method, payload));
  } catch (error) {
    if (error?.status === 422 && error?.data?.summary) {
      return normalizeBulkResponse({ message: error.message, data: error.data });
    }
    throw error;
  }
};

const STATUS_LABELS = {
  duplicate: "Sudah ada (dobel)",
  not_found: "Data tidak ditemukan",
  conflict: "Konflik",
  validation: "Data tidak valid",
  server_error: "Error server",
  skipped: "Tidak diproses (dibatalkan semua)",
};

export const getBulkStatusLabel = (result) => {
  if (result?.code === "DUPLICATE_IN_REQUEST") return "Dobel di pilihan yang sama";
  return STATUS_LABELS[result?.status] || result?.status || "Gagal";
};

// Pasangkan hasil per item ke baris yang dikirim (berdasarkan index), hanya yang tidak "ok".
export const getBulkFailures = (results, items, getLabel) => results
  .filter((result) => result.status !== "ok")
  .map((result) => ({
    result,
    item: items[result.index],
    label: getLabel(items[result.index], result.index),
    reason: getBulkStatusLabel(result),
    message: result.message || "",
  }));

const escapeHtml = (value) => String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const buildBulkFailureHtml = (failures, limit = 10) => {
  const lines = failures.slice(0, limit).map((failure) => (
    `• <b>${escapeHtml(failure.label)}</b> — ${escapeHtml(failure.reason)}`
  ));
  if (failures.length > limit) lines.push(`• ... dan ${failures.length - limit} lainnya`);
  return `<div style="text-align:left;font-size:13px;max-height:260px;overflow:auto">${lines.join("<br/>")}</div>`;
};

// Pecah item > 500 menjadi beberapa request (berurutan, supaya urutan sort_order tetap terjaga).
export const chunkItems = (items, size = BULK_LIMIT) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) chunks.push(items.slice(index, index + size));
  return chunks;
};

// Jalankan bulk per chunk dan gabungkan hasilnya; index dikembalikan ke posisi global.
export const runChunkedBulkRequest = async ({ url, method, items, buildPayload }) => {
  const merged = { results: [], rolledBack: false, okCount: 0 };
  let offset = 0;
  for (const chunk of chunkItems(items)) {
    // eslint-disable-next-line no-await-in-loop
    const response = await runBulkRequest({ url, method, payload: buildPayload(chunk) });
    const chunkOffset = offset;
    merged.results.push(...response.results.map((result) => ({ ...result, index: result.index + chunkOffset })));
    merged.rolledBack = merged.rolledBack || response.rolledBack;
    offset += chunk.length;
  }
  merged.okCount = merged.results.filter((result) => result.status === "ok").length;
  merged.allOk = merged.results.length === items.length && merged.okCount === items.length;
  return merged;
};
